import { STAGING_BANNER_HEIGHT, isStagingEnv } from './StagingBanner';
import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Container,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  AccountCircle,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Favorite as FavoriteIcon,
  Close as CloseIcon,
  Login as LoginIcon,
  Article as BlogIcon,
  AutoAwesome as AIIcon,
  Gavel as GavelIcon,
  Stars as StarsIcon,
  WorkspacePremium as WorkIcon,
} from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';
import axios from 'axios';

interface NavbarProps {
  onMenuClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuClick }) => {
  const { user, isAuthenticated, isAdmin, logout, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  useEffect(() => {
    if (isAuthenticated() && token) {
      axios.get('/api/credits/balance', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCreditBalance(r.data.credits))
        .catch(() => setCreditBalance(null));
    }
  }, [isAuthenticated(), token]);

  // Le pagine Booky notificano il nuovo saldo dopo ogni ricerca/analisi
  useEffect(() => {
    const onCreditsUpdated = (e: Event) => setCreditBalance((e as CustomEvent<number>).detail);
    window.addEventListener('credits-updated', onCreditsUpdated);
    return () => window.removeEventListener('credits-updated', onCreditsUpdated);
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    setMobileDrawerOpen(false);
    await logout();
    navigate('/login');
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Menu items per mobile drawer
  const mobileMenuItems = [
    // Blog visibile a tutti
    // { text: 'Blog', icon: <BlogIcon />, path: '/blog' },
    ...(isAuthenticated() ? [
      { text: 'BOOKY SEARCH', icon: <AIIcon />, path: '/booky-search', highlight: true },
      { text: 'BOOKY TRIBUNALE', icon: <GavelIcon />, path: '/booky-tribunale', highlight: true },
      // { text: 'BOOKY WORK', icon: <WorkIcon />, path: '/booky-work', highlight: true },
      // { text: 'BOOKY GIUDICE', icon: <GavelIcon />, path: '/booky-giudice', highlight: true },
      { text: 'Preferiti', icon: <FavoriteIcon />, path: '/favorites' },
      { text: `Crediti${creditBalance !== null ? ` (${creditBalance})` : ''}`, icon: <StarsIcon sx={{ color: '#FFD700' }} />, path: '/credits', highlight: true },
    ] : []),
    ...(isAdmin() ? [
      { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
    ] : []),
  ];

  return (
    <>
    <AppBar 
      position="fixed" 
        elevation={1}
      sx={{ 
          backgroundColor: 'var(--primary-color)',
        borderRadius: 0,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        top: isStagingEnv ? `${STAGING_BANNER_HEIGHT}px` : 0,
      }}
    >
      <Container maxWidth={false}>
          <Toolbar 
            sx={{ 
              justifyContent: 'space-between',
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 2 },
            }}
          >
            {/* Sinistra: Hamburger (per sidebar) + Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Hamburger per aprire Sidebar - solo se autenticato */}
              {isAuthenticated() && (
            <IconButton
              color="inherit"
                  aria-label="apri menu laterale"
              edge="start"
              onClick={onMenuClick}
                  sx={{ 
                    mr: { xs: 1, sm: 2 },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
            >
              <MenuIcon />
            </IconButton>
              )}
              
              {/* Logo */}
            <Box
              component={Link}
              to={isAuthenticated() ? "/dashboard" : "/login"}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
                <Box
                  component="img"
                src="/itfpluslogo.png" 
                alt="ITFPLUS" 
                  sx={{ 
                    height: { xs: 32, sm: 40 },
                    width: 'auto',
                }} 
              />
            </Box>
          </Box>

            {/* Destra: Menu desktop o hamburger mobile */}
            {!isMobile ? (
              // DESKTOP: mostra bottoni
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Blog - visibile a tutti */}
                {/* <Button
                  component={Link}
                  to="/blog"
                  color="inherit"
                  startIcon={<BlogIcon />}
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Blog
                </Button> */}

            {isAuthenticated() && (
              <>
                <Button
                  component={Link}
                  to="/booky-search"
                  color="inherit"
                  startIcon={<AIIcon />}
                  sx={{ 
                    background: 'linear-gradient(90deg, rgba(255,215,0,0.15) 0%, rgba(255,193,7,0.1) 100%)',
                    border: '1px solid rgba(255,215,0,0.3)',
                    borderRadius: '4px',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(90deg, rgba(255,215,0,0.25) 0%, rgba(255,193,7,0.2) 100%)',
                    },
                  }}
                >
                  BOOKY SEARCH
                </Button>
                <Button
                  component={Link}
                  to="/booky-tribunale"
                  color="inherit"
                  startIcon={<GavelIcon />}
                  sx={{ 
                    background: 'linear-gradient(90deg, rgba(123,31,162,0.15) 0%, rgba(156,39,176,0.1) 100%)',
                    border: '1px solid rgba(123,31,162,0.3)',
                    borderRadius: '4px',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(90deg, rgba(123,31,162,0.25) 0%, rgba(156,39,176,0.2) 100%)',
                    },
                  }}
                >
                  BOOKY TRIBUNALE
                </Button>
                {/* <Button
                  component={Link}
                  to="/booky-work"
                  color="inherit"
                  startIcon={<WorkIcon />}
                  sx={{ 
                    background: 'linear-gradient(90deg, rgba(37,99,235,0.15) 0%, rgba(124,58,237,0.1) 100%)',
                    border: '1px solid rgba(37,99,235,0.3)',
                    borderRadius: '4px',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(90deg, rgba(37,99,235,0.25) 0%, rgba(124,58,237,0.2) 100%)',
                    },
                  }}
                >
                  BOOKY WORK
                </Button>
                <Button
                  component={Link}
                  to="/booky-giudice"
                  color="inherit"
                  startIcon={<GavelIcon />}
                  sx={{
                    background: 'linear-gradient(90deg, rgba(184,134,11,0.2) 0%, rgba(218,165,32,0.1) 100%)',
                    border: '1px solid rgba(184,134,11,0.4)',
                    borderRadius: '4px',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(90deg, rgba(184,134,11,0.35) 0%, rgba(218,165,32,0.2) 100%)',
                    },
                  }}
                >
                  BOOKY GIUDICE
                </Button> */}
              <Button
                component={Link}
                to="/favorites"
                color="inherit"
                startIcon={<FavoriteIcon />}
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
              >
                Preferiti
              </Button>

              {/* Bottone Crediti con contatore */}
              <Button
                component={Link}
                to="/credits"
                color="inherit"
                startIcon={<StarsIcon sx={{ color: '#FFD700' }} />}
                sx={{
                  background: 'linear-gradient(90deg, rgba(255,215,0,0.15) 0%, rgba(99,102,241,0.15) 100%)',
                  border: '1px solid rgba(255,215,0,0.4)',
                  borderRadius: '4px',
                  fontWeight: 700,
                  gap: 0.5,
                  '&:hover': {
                    background: 'linear-gradient(90deg, rgba(255,215,0,0.25) 0%, rgba(99,102,241,0.25) 100%)',
                  },
                }}
              >
                {creditBalance !== null ? (
                  <Chip
                    label={creditBalance}
                    size="small"
                    sx={{
                      bgcolor: '#FFD700',
                      color: '#1B2A4A',
                      fontWeight: 800,
                      fontSize: '0.75rem',
                      height: 20,
                      ml: 0.5,
                    }}
                  />
                ) : 'Crediti'}
              </Button>
              </>
            )}

            {isAdmin() && (
              <Button
                component={Link}
                to="/admin"
                color="inherit"
                startIcon={<AdminIcon />}
                    sx={{ 
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
              >
                Admin
              </Button>
            )}

            {isAuthenticated() ? (
              <Box>
                <IconButton
                  size="large"
                      aria-label="menu account"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                      <Avatar 
                        sx={{ 
                          width: 34, 
                          height: 34, 
                          backgroundColor: 'var(--secondary-color)', 
                          color: 'white',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          borderRadius: '4px',
                        }}
                      >
                        {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          minWidth: 180,
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                          border: '1px solid var(--border-light)',
                        }
                      }}
                >
                      <MenuItem 
                        onClick={handleLogout}
                        sx={{ 
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(244, 63, 94, 0.1)',
                          },
                        }}
                      >
                        <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--error-color)' }} />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Button
                component={Link}
                to="/login"
                color="inherit"
                variant="outlined"
                    sx={{ 
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
              >
                Accedi
              </Button>
            )}
          </Box>
            ) : (
              // MOBILE: mostra hamburger per drawer
            <IconButton
              color="inherit"
                aria-label="apri menu"
                onClick={handleMobileDrawerToggle}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {mobileDrawerOpen ? <CloseIcon /> : <AccountCircle />}
            </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            pt: '64px',
            backgroundColor: 'var(--card-background)',
            borderRadius: 0,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {isAuthenticated() && user && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 2,
                  p: 2,
                  borderRadius: '4px',
                  backgroundColor: 'rgba(27, 42, 74, 0.04)',
                  border: '1px solid var(--border-light)',
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 44, 
                    height: 44, 
                    backgroundColor: 'var(--primary-color)', 
                    color: 'white',
                    fontWeight: 600,
                    borderRadius: '4px',
              }}
                >
                {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Box>
                <Box sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  {user.name || 'Utente'}
                </Box>
                <Box sx={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {user.email}
                </Box>
              </Box>
            </Box>
              )}

          <Divider sx={{ my: 2 }} />

          <List disablePadding>
            {mobileMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={() => setMobileDrawerOpen(false)}
                  sx={{
                    borderRadius: '4px',
                    mb: 0.5,
                    ...((item as any).highlight && {
                      background: 'linear-gradient(90deg, rgba(255,215,0,0.15) 0%, rgba(255,193,7,0.1) 100%)',
                      border: '1px solid rgba(255,215,0,0.3)',
                    }),
                    '&:hover': {
                      backgroundColor: (item as any).highlight 
                        ? 'rgba(255,215,0,0.25)' 
                        : 'rgba(27, 42, 74, 0.06)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: 40, 
                    color: (item as any).highlight ? '#6366F1' : 'var(--primary-color)' 
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{ 
                      fontWeight: (item as any).highlight ? 600 : 500,
                      color: (item as any).highlight ? '#6366F1' : 'inherit'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

            <Divider sx={{ my: 2 }} />

              {isAuthenticated() ? (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: '4px',
                    color: 'var(--error-color)',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 63, 94, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'var(--error-color)' }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Logout"
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              </ListItem>
              ) : (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/login"
                  onClick={() => setMobileDrawerOpen(false)}
                  sx={{
                    borderRadius: '4px',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'var(--primary-light)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                    <LoginIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Accedi"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>
              </ListItem>
              )}
          </List>
          </Box>
      </Drawer>
    </>
  );
};

export default Navbar; 
